import { useNavigate } from "react-router-dom";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { canManageReport } from "@/profitability/access";
import {
  profitabilityRunPath,
  profitabilityTemplatePath,
} from "@/profitability/routes";
import { VersionsSection } from "@/profitability/template-chat/workspace/versions-section";
import { BuildHistory } from "@/profitability/template-page/build-history";
import type { ProfitabilityReport } from "@/profitability/types";
import { AgentInstructions } from "./agent-instructions";
import { AgentSources } from "./agent-sources";
import { AgentSummary } from "./agent-summary";
import { MatchingGroup } from "./matching-group";
import { OwnersGroup } from "./owners-group";
import { PeriodGroup } from "./period-group";
import { RatesGroup } from "./rates-group";
import { ReviewRulesGroup } from "./review-rules-group";
import { RuleDetail } from "./rule-detail";
import { SettingsGroup } from "./settings-group";
import { SettingsLoadError } from "./settings-load-error";
import type { SettingsPage } from "./settings-nav";
import { ArchiveGroup, SharingGroup } from "./sharing-group";
import { useSettingsState } from "./use-settings-state";

export const SettingsTab = ({
  businessId,
  report,
  page,
  ruleId,
}: {
  businessId: string;
  report: ProfitabilityReport;
  page: SettingsPage;
  ruleId?: string;
}) => {
  const state = useSettingsState(businessId, report);
  const manages = canManageReport(report);
  const failed = state.failed ? (
    <SettingsLoadError label="Settings could not load." retry={state.retry} />
  ) : null;
  return (
    <Stack gap={8}>
      {page === "general" && (
        <>
          <AgentSummary report={report} />
          <AgentSources report={report} />
          <PeriodGroup businessId={businessId} report={report} />
          {failed ?? <MatchingGroup state={state} report={report} />}
          <AgentInstructions businessId={businessId} report={report} />
          {manages && <ArchiveGroup businessId={businessId} report={report} />}
        </>
      )}
      {page === "calculations" && (failed ?? <RatesGroup state={state} />)}
      {page === "rules" &&
        (failed ??
          (ruleId ? (
            <RuleDetail
              key={ruleId}
              businessId={businessId}
              report={report}
              ruleId={ruleId}
              state={state}
            />
          ) : (
            <ReviewRulesGroup
              reportId={report.id}
              state={state}
              canAsk={manages && !report.archived_at}
            />
          )))}
      {page === "people" && (
        <>
          {manages && <SharingGroup businessId={businessId} report={report} />}
          {failed ??
            (!state.settings ? (
              <Skeleton
                className="h-32 w-full"
                role="status"
                aria-label="Loading assigned reviewers"
              />
            ) : (
              <OwnersGroup
                state={state}
                noun={
                  state.settings.rules?.[0]?.on_label?.toLowerCase() ??
                  "records"
                }
              />
            ))}
        </>
      )}
      {page === "versions" && manages && (
        <HistoryGroups businessId={businessId} report={report} />
      )}
    </Stack>
  );
};

const HistoryGroups = ({
  businessId,
  report,
}: {
  businessId: string;
  report: ProfitabilityReport;
}) => {
  const navigate = useNavigate();
  return (
    <>
      <VersionsSection
        businessId={businessId}
        report={report}
        onView={(runId) => navigate(profitabilityRunPath(runId))}
        onRestored={() => navigate(profitabilityTemplatePath(report.id))}
      />
      <SettingsGroup
        title="Build history"
        description="Build attempts and their results."
      >
        <Stack className="p-4">
          <BuildHistory
            businessId={businessId}
            reportId={report.id}
            buildStatus={report.build_status}
            titled={false}
          />
        </Stack>
      </SettingsGroup>
    </>
  );
};
